import { EXECUTION_STATE } from '@src/models/execution';
import { DateUtil } from '@src/packages/utils/dates';

export interface StateTransitionRecord {
  id?: string | null;
  fromState?: string | null;
  toState?: string | null;
  timestamp?: unknown;
  userId?: string | null;
  uid?: string | null;
  reason?: string | null;
}

export interface DelayRow {
  key: string;
  userId: string | null;
  reason: string | null;
  startedAt: Date;
  /** null while the delay is still running. */
  endedAt: Date | null;
  /** Elapsed time so far for a delay still running, matching how the backend
   *  totals `totalStalledDurationMs`. */
  durationSeconds: number;
}

interface TimedTransition {
  transition: StateTransitionRecord;
  timestampMs: number;
}

const getTransitionTimestampMs = (transition: StateTransitionRecord): number | null =>
  DateUtil.getTimestampMs(transition.timestamp);

/** Builds one row per entry into the stalled state and pairs it with its exit, if present.
 *  A delay still running is measured against `now`: the backend counts the in-progress
 *  delay in `totalStalledDurationMs` too, so both totals follow the same criterion. */
export const buildDelayRows = (
  transitions: StateTransitionRecord[],
  now: number = Date.now(),
): DelayRow[] => {
  const timedTransitions: TimedTransition[] = transitions
    .map(transition => ({
      transition,
      timestampMs: getTransitionTimestampMs(transition),
    }))
    .filter(
      (transition): transition is TimedTransition => transition.timestampMs !== null,
    )
    .sort((first, second) => first.timestampMs - second.timestampMs);

  const delays = timedTransitions.flatMap(({ transition, timestampMs }, index) => {
    if (transition.toState !== EXECUTION_STATE.stalled) {
      return [];
    }

    const exitTransition = timedTransitions.slice(index + 1).find(({ transition: next }) =>
      next.fromState === EXECUTION_STATE.stalled && next.toState !== EXECUTION_STATE.stalled,
    );
    const endedAt = exitTransition ? new Date(exitTransition.timestampMs) : null;
    const durationSeconds = Math.max(
      0,
      Math.floor(((endedAt ? endedAt.getTime() : now) - timestampMs) / 1000),
    );
    const userId = transition.userId ?? transition.uid ?? null;

    return [
      {
        key:
          transition.id ??
          `${timestampMs}-${userId ?? 'system'}-${transition.reason ?? 'no-reason'}`,
        userId,
        reason: transition.reason?.trim() || null,
        startedAt: new Date(timestampMs),
        endedAt,
        durationSeconds,
      },
    ];
  });

  return delays.sort((first, second) => second.startedAt.getTime() - first.startedAt.getTime());
};

export const getDelaysDurationSeconds = (delayRows: DelayRow[]): number =>
  delayRows.reduce((total, delay) => total + delay.durationSeconds, 0);
